import { Fragment } from 'react';
import { CmsComponentProps } from '../../types';
import { MultiProductBlock } from './MultiProductBlock';
import { MultiProductBlockPlacement } from './types';
import {
  ComponentMultiProductBlock,
  PageType,
  VerticalType,
} from '$/(cms)/_modules/cmsService/types';

type MultiProductBlockWrapperBaseProps = {
  pageType: PageType;
  vertical?: VerticalType;
  slug?: string;
};

type MultiProductBlockWrapperSingleProps = MultiProductBlockWrapperBaseProps & {
  mode: 'single';
  block: ComponentMultiProductBlock;
  placement?: MultiProductBlockPlacement;
};

type MultiProductBlockWrapperCollectionProps =
  MultiProductBlockWrapperBaseProps & {
    mode: 'collection';
    components: CmsComponentProps[];
    placement: 'right-rail';
  };

type MultiProductBlockWrapperProps =
  | MultiProductBlockWrapperSingleProps
  | MultiProductBlockWrapperCollectionProps;

const isMultiProductBlock = (
  component: CmsComponentProps,
): component is ComponentMultiProductBlock => {
  return (
    component.__typename === 'ComponentPagesComponentMultiProductBlockComponent'
  );
};

const shouldRenderMultiProductBlock = (params: {
  block: ComponentMultiProductBlock;
  pageType: PageType;
  placement: MultiProductBlockPlacement;
}): boolean => {
  const { block, pageType, placement } = params;
  const visible = (block as { visible?: boolean }).visible;
  const isDesktopEnabled = block.visibility_desktop !== false;
  const isMobileEnabled = block.visibility_mobile !== false;

  if (visible === false || pageType !== 'article') {
    return false;
  }

  if (placement === 'right-rail') {
    return isDesktopEnabled;
  }

  return isMobileEnabled;
};

export const MultiProductBlockWrapper: React.FC<
  MultiProductBlockWrapperProps
> = (props) => {
  if (props.mode === 'single') {
    const placement = props.placement ?? 'inline';
    if (
      !shouldRenderMultiProductBlock({
        block: props.block,
        pageType: props.pageType,
        placement,
      })
    ) {
      return null;
    }

    return (
      <MultiProductBlock
        {...props.block}
        pageType={props.pageType}
        vertical={props.vertical}
        slug={props.slug}
        placement={placement}
      />
    );
  }

  const blocks = props.components.filter(isMultiProductBlock).filter((block) =>
    shouldRenderMultiProductBlock({
      block,
      pageType: props.pageType,
      placement: props.placement,
    }),
  );

  return (
    <>
      {blocks.map((block, index) => (
        <Fragment key={`${block.id ?? index}`}>
          <MultiProductBlock
            {...block}
            pageType={props.pageType}
            vertical={props.vertical}
            slug={props.slug}
            placement={props.placement}
          />
        </Fragment>
      ))}
    </>
  );
};
